import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/errors';
import type {
  DocumentFolder as GeneratedDocumentFolder,
} from '@/api/generated/client/models/DocumentFolder';

// Re-export the generated type so consumers get the real type
export type DocumentFolder = GeneratedDocumentFolder;

// ── Param interfaces ─────────────────────────────────────────────

interface CreateFolderParams {
  name: string;
  parentId?: number | null;
  color?: string | null;
}

interface UpdateFolderParams {
  folderId: number;
  name?: string;
  parentId?: number | null;
  color?: string | null;
}

interface MoveDocumentParams {
  documentId: number;
  folderId: number | null;
}

// ── Hook ─────────────────────────────────────────────────────────

export function useDocumentFolders(claimId: number) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // ── Query: list folders ──
  const { data, isLoading, error } = useQuery({
    queryKey: ['document-folders', claimId],
    queryFn: async () => {
      const res = await apiClient.documents.listDocumentFolders({ claimId });
      return res.items;
    },
    enabled: !!claimId,
  });

  const folders: DocumentFolder[] = data ?? [];

  // ── Mutation: create folder ──
  const createFolderMutation = useMutation({
    mutationFn: async (params: CreateFolderParams) => {
      return apiClient.documents.createDocumentFolder({
        claimId,
        createDocumentFolderRequest: params,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folders', claimId] });
      toast({ title: 'Folder Created', description: 'New folder added successfully.' });
    },
    onError: async (error: Error) => {
      toast({ title: 'Failed to Create Folder', description: await getApiErrorMessage(error), variant: 'destructive' });
    },
  });

  // ── Mutation: update folder ──
  const updateFolderMutation = useMutation({
    mutationFn: async ({ folderId, ...body }: UpdateFolderParams) => {
      return apiClient.documents.updateDocumentFolder({
        claimId,
        folderId,
        updateDocumentFolderRequest: body,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folders', claimId] });
    },
    onError: async (error: Error) => {
      toast({ title: 'Failed to Update Folder', description: await getApiErrorMessage(error), variant: 'destructive' });
    },
  });

  // ── Mutation: delete folder ──
  const deleteFolderMutation = useMutation({
    mutationFn: async (folderId: number) => {
      await apiClient.documents.deleteDocumentFolder({ claimId, folderId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folders', claimId] });
      queryClient.invalidateQueries({ queryKey: ['documents', claimId] });
      toast({ title: 'Folder Deleted', description: 'Folder removed successfully.' });
    },
    onError: async (error: Error) => {
      toast({ title: 'Failed to Delete Folder', description: await getApiErrorMessage(error), variant: 'destructive' });
    },
  });

  // ── Mutation: move document to folder ──
  const moveDocumentMutation = useMutation({
    mutationFn: async (params: MoveDocumentParams) => {
      await apiClient.documents.moveDocumentToFolder({
        claimId,
        moveDocumentToFolderRequest: params,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folders', claimId] });
      queryClient.invalidateQueries({ queryKey: ['documents', claimId] });
    },
    onError: async (error: Error) => {
      toast({ title: 'Failed to Move Document', description: await getApiErrorMessage(error), variant: 'destructive' });
    },
  });

  return {
    folders,
    isLoading,
    error,
    createFolder: createFolderMutation.mutateAsync,
    isCreating: createFolderMutation.isPending,
    updateFolder: updateFolderMutation.mutateAsync,
    isUpdating: updateFolderMutation.isPending,
    deleteFolder: deleteFolderMutation.mutateAsync,
    isDeleting: deleteFolderMutation.isPending,
    moveDocument: moveDocumentMutation.mutateAsync,
    isMoving: moveDocumentMutation.isPending,
  };
}

// ── Shared tree UI state ─────────────────────────────────────────
// Manages expand/collapse, inline rename, and inline create for any
// tree of items with { id: number; name: string }.

export function useFolderTree() {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [creatingInParent, setCreatingInParent] = useState<number | null | "root">(null);
  const [newName, setNewName] = useState("");
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const toggleExpanded = useCallback((id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const expandNode = useCallback((id: number) => {
    setExpandedIds(prev => new Set(prev).add(id));
  }, []);

  const startCreate = useCallback((parentId: number | null | "root") => {
    setCreatingInParent(parentId === null ? "root" : parentId);
    setNewName("");
    if (typeof parentId === "number") {
      setExpandedIds(prev => new Set(prev).add(parentId));
    }
  }, []);

  const cancelCreate = useCallback(() => {
    setCreatingInParent(null);
    setNewName("");
  }, []);

  const startRename = useCallback((id: number, currentName: string) => {
    setRenamingId(id);
    setRenameValue(currentName);
  }, []);

  const cancelRename = useCallback(() => {
    setRenamingId(null);
    setRenameValue("");
  }, []);

  return {
    expandedIds,
    toggleExpanded,
    expandNode,
    creatingInParent,
    startCreate,
    cancelCreate,
    newName,
    setNewName,
    renamingId,
    renameValue,
    setRenameValue,
    startRename,
    cancelRename,
  };
}

// ── Drag-and-drop state for documents and folders ────────────────

interface MoveFolderParams {
  folderId: number;
  parentId: number | null;
}

export function useDocumentDragDrop(
  moveDocument: (params: MoveDocumentParams) => Promise<unknown>,
  moveFolder: (params: MoveFolderParams) => Promise<unknown>,
) {
  const [draggingDocId, setDraggingDocId] = useState<number | null>(null);
  const [draggingFolderId, setDraggingFolderId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null | "root">(null);

  const handleDragStart = useCallback(
    (docId: number) => (e: React.DragEvent) => {
      setDraggingDocId(docId);
      setDraggingFolderId(null);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("application/x-doc-id", String(docId));
    },
    [],
  );

  const handleFolderDragStart = useCallback(
    (folderId: number) => (e: React.DragEvent) => {
      setDraggingFolderId(folderId);
      setDraggingDocId(null);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("application/x-folder-id", String(folderId));
    },
    [],
  );

  const handleDragEnd = useCallback(() => {
    setDraggingDocId(null);
    setDraggingFolderId(null);
    setDropTargetId(null);
  }, []);

  const handleDrop = useCallback(
    (targetFolderId: number | null) => (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDropTargetId(null);

      const docIdStr = e.dataTransfer.getData("application/x-doc-id");
      if (docIdStr) {
        const docId = Number(docIdStr);
        if (docId) moveDocument({ documentId: docId, folderId: targetFolderId }).catch(() => {});
        return;
      }

      const folderIdStr = e.dataTransfer.getData("application/x-folder-id");
      if (folderIdStr) {
        const folderId = Number(folderIdStr);
        if (folderId && folderId !== targetFolderId) {
          moveFolder({ folderId, parentId: targetFolderId }).catch(() => {});
        }
      }
    },
    [moveDocument, moveFolder],
  );

  const onDragOver = useCallback(
    (id: number | "root") => (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "move";
      setDropTargetId(id);
    },
    [],
  );

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.stopPropagation();
    setDropTargetId(null);
  }, []);

  return {
    draggingDocId,
    draggingFolderId,
    dropTargetId,
    handleDragStart,
    handleFolderDragStart,
    handleDragEnd,
    handleDrop,
    onDragOver,
    onDragLeave,
  };
}
